import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, Server, Database, HardDrive, Activity, Users, 
  Link2, Mail, Shield, Clock, CheckCircle, XCircle,
  Cpu, MemoryStick, Settings, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

// DetailRow component matching ServerDetail pattern
const DetailRow = ({ icon: Icon, label, value, mono = false, highlight = false }) => (
  <div className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
    {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
    <div className="flex-1 min-w-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className={`text-sm truncate ${mono ? 'font-mono' : ''} ${highlight ? 'text-primary font-medium' : 'text-foreground'}`}>
        {value || '-'}
      </p>
    </div>
  </div>
);

const SqlServerDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [server, setServer] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  // Tab data
  const [databases, setDatabases] = useState([]);
  const [backups, setBackups] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logins, setLogins] = useState([]);
  const [linkedServers, setLinkedServers] = useState([]);
  const [dbMail, setDbMail] = useState([]);
  
  // DB Users modal
  const [dbUsersModal, setDbUsersModal] = useState({ open: false, dbName: '', users: [] });
  
  // Filters
  const [dbSearch, setDbSearch] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('');

  useEffect(() => {
    fetchServer();
  }, [id]);

  useEffect(() => {
    if (server) {
      fetchTabData(activeTab);
    }
  }, [activeTab, server]);

  const fetchServer = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/sql/${id}`);
      setServer(response.data.server);
    } catch (error) {
      toast.error('Error al cargar SQL Server');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    try {
      switch (tab) {
        case 'databases':
          const dbRes = await api.get(`/assets/sql/${id}/databases`);
          setDatabases(dbRes.data.items);
          break;
        case 'backups':
          const bkRes = await api.get(`/assets/sql/${id}/backups`);
          setBackups(bkRes.data.items);
          break;
        case 'jobs':
          const jobRes = await api.get(`/assets/sql/${id}/jobs`);
          setJobs(jobRes.data.items);
          break;
        case 'logins':
          const loginRes = await api.get(`/assets/sql/${id}/usuarios`);
          setLogins(loginRes.data.items);
          break;
        case 'linkedservers':
          const lsRes = await api.get(`/assets/sql/${id}/linkedservers`);
          setLinkedServers(lsRes.data.items);
          break;
        case 'dbmail':
          const mailRes = await api.get(`/assets/sql/${id}/dbmail`);
          setDbMail(mailRes.data.items);
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
    }
  };

  const fetchDbUsers = async (dbName) => {
    try {
      const response = await api.get(`/assets/sql/${id}/db-usuarios?database_name=${encodeURIComponent(dbName)}`);
      setDbUsersModal({ open: true, dbName, users: response.data.items });
    } catch (error) {
      toast.error('Error al cargar usuarios de la base de datos');
    }
  };

  const formatDate = (date) => formatDateARG(date);

  const formatSize = (mb) => {
    if (!mb) return '-';
    const num = parseFloat(mb);
    if (num >= 1024) return `${(num / 1024).toFixed(2)} GB`;
    return `${num.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!server) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">SQL Server no encontrado</p>
          <Link to="/assets/sql">
            <Button variant="link" className="mt-4 text-primary">Volver al listado</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Filter databases
  const filteredDatabases = databases.filter(db => 
    !dbSearch || db.database_name?.toLowerCase().includes(dbSearch.toLowerCase())
  );

  // Filter jobs
  const filteredJobs = jobs.filter(job =>
    !jobStatusFilter || job.last_run_status === jobStatusFilter
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/assets/sql">
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="page-title flex items-center gap-3">
                <Database className="h-6 w-6 text-primary" />
                {server.display_name || server.hostname}
              </h1>
              <p className="page-subtitle">
                {server.sql_edition || 'SQL Server'}
                {server.instance_name && <span className="ml-2 text-primary">({server.instance_name})</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <Card className="nerve-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">IP</p>
                <p className="font-mono text-lg text-primary">{server.ip || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Version</p>
                <p className="text-lg font-medium">{server.product_level || '-'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">CPU / RAM</p>
                <p className="font-mono text-lg">
                  {server.cpu_count || '-'} cores / {server.physical_memory_mb ? Math.round(server.physical_memory_mb / 1024) : '-'} GB
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Tipo</p>
                <span className={`badge ${server.virtual_machine ? 'badge-info' : 'badge-success'}`}>
                  {server.virtual_machine ? 'Virtual' : 'Fisico'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="nerve-card p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Settings className="h-4 w-4 mr-1.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="databases" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Database className="h-4 w-4 mr-1.5" />
              Databases
            </TabsTrigger>
            <TabsTrigger value="backups" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <HardDrive className="h-4 w-4 mr-1.5" />
              Backups
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Activity className="h-4 w-4 mr-1.5" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="logins" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Shield className="h-4 w-4 mr-1.5" />
              Logins
            </TabsTrigger>
            <TabsTrigger value="linkedservers" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Link2 className="h-4 w-4 mr-1.5" />
              Linked Servers
            </TabsTrigger>
            <TabsTrigger value="dbmail" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-md text-sm">
              <Mail className="h-4 w-4 mr-1.5" />
              DB Mail
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* SQL Server Info */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Database className="h-4 w-4 text-primary" />
                    Informacion SQL Server
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-1">
                    <div className="py-2 border-b border-border/50">
                      <span className="text-xs text-muted-foreground">Version Completa</span>
                      <p className="text-xs font-mono text-foreground whitespace-pre-wrap">{server.sql_version || '-'}</p>
                    </div>
                  </div>
                  <DetailRow icon={Shield} label="Edicion" value={server.sql_edition} />
                  <DetailRow icon={Server} label="Product Level" value={server.product_level} />
                  <DetailRow icon={Database} label="Collation" value={server.collation} />
                  <DetailRow icon={Clock} label="Ultimo Reinicio" value={formatDate(server.last_startup)} />
                </CardContent>
              </Card>

              {/* Resources */}
              <Card className="nerve-card">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-primary" />
                    Recursos
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <DetailRow icon={Cpu} label="CPU Count" value={server.cpu_count} />
                  <DetailRow icon={MemoryStick} label="Memoria Fisica" value={`${server.physical_memory_mb || 0} MB`} />
                  <DetailRow icon={MemoryStick} label="Max Memory SQL" value={server.max_memory_mb === 2147483647 ? 'Unlimited' : `${server.max_memory_mb} MB`} />
                  <DetailRow icon={MemoryStick} label="Min Memory SQL" value={`${server.min_memory_mb || 0} MB`} />
                  <DetailRow icon={Activity} label="MAXDOP" value={server.max_dop} />
                  <DetailRow icon={Activity} label="Cost Threshold" value={server.cost_threshold} />
                </CardContent>
              </Card>

              {/* Service Accounts */}
              <Card className="nerve-card md:col-span-2">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Cuentas de Servicio
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DetailRow icon={Server} label="SQL Service Account" value={server.sql_service_account} mono />
                    <DetailRow icon={Activity} label="SQL Agent Account" value={server.sql_agent_account} mono />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Databases Tab */}
          <TabsContent value="databases">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>DATABASE</th>
                        <th className="text-center">ESTADO</th>
                        <th className="text-center">RECOVERY</th>
                        <th className="text-right">TAMANO</th>
                        <th className="text-right">DATA</th>
                        <th className="text-right">LOG</th>
                        <th className="text-center">COMPAT</th>
                        <th className="w-20 text-center">USUARIOS</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={dbSearch}
                            onChange={(e) => setDbSearch(e.target.value)}
                            className="h-7 text-xs bg-background"
                          />
                        </th>
                        <th colSpan={7}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDatabases.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No hay bases de datos</p>
                            </div>
                          </td>
                        </tr>
                      ) : filteredDatabases.map((db) => (
                        <tr key={db.id}>
                          <td className="font-medium text-primary">{db.database_name}</td>
                          <td className="text-center">
                            <span className={`badge ${db.state === 'ONLINE' ? 'badge-success' : 'badge-error'}`}>
                              {db.state}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-outline">{db.recovery_model}</span>
                          </td>
                          <td className="text-right font-mono text-sm">{formatSize(db.size_mb)}</td>
                          <td className="text-right font-mono text-sm text-muted-foreground">{formatSize(db.data_size_mb)}</td>
                          <td className="text-right font-mono text-sm text-muted-foreground">{formatSize(db.log_size_mb)}</td>
                          <td className="text-center text-muted-foreground">{db.compatibility_level}</td>
                          <td className="text-center">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-muted"
                              onClick={() => fetchDbUsers(db.database_name)}
                              title="Ver usuarios de esta BD"
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backups Tab */}
          <TabsContent value="backups">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>DATABASE</th>
                        <th className="text-center">TIPO</th>
                        <th>INICIO</th>
                        <th className="text-right">TAMANO</th>
                        <th className="text-right">COMPRIMIDO</th>
                        <th className="text-center">DIAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <div className="empty-state">
                              <HardDrive className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No hay backups registrados</p>
                            </div>
                          </td>
                        </tr>
                      ) : backups.map((bk) => (
                        <tr key={bk.id}>
                          <td className="font-medium">{bk.database_name}</td>
                          <td className="text-center">
                            <span className={`badge ${bk.backup_type === 'FULL' ? 'badge-info' : 'badge-outline'}`}>
                              {bk.backup_type}
                            </span>
                          </td>
                          <td className="text-muted-foreground text-sm">{formatDate(bk.backup_start)}</td>
                          <td className="text-right font-mono text-sm">{formatSize(bk.backup_size_mb)}</td>
                          <td className="text-right font-mono text-sm text-primary">{formatSize(bk.compressed_size_mb)}</td>
                          <td className="text-center">
                            <span className={`badge ${bk.days_since_backup <= 1 ? 'badge-success' : bk.days_since_backup <= 7 ? 'badge-warning' : 'badge-error'}`}>
                              {bk.days_since_backup}d
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>JOB</th>
                        <th className="text-center">HABILITADO</th>
                        <th className="text-center">ESTADO</th>
                        <th>ULTIMA EJECUCION</th>
                        <th className="text-right">DURACION</th>
                        <th>PROXIMA EJECUCION</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4" colSpan={2}></th>
                        <th className="py-2 px-4">
                          <select 
                            className="h-7 text-xs bg-background border border-input rounded px-2 w-full"
                            value={jobStatusFilter}
                            onChange={(e) => setJobStatusFilter(e.target.value)}
                          >
                            <option value="">Todos</option>
                            <option value="Succeeded">Succeeded</option>
                            <option value="Failed">Failed</option>
                          </select>
                        </th>
                        <th colSpan={3}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobs.length === 0 ? (
                        <tr>
                          <td colSpan={6}>
                            <div className="empty-state">
                              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No hay jobs registrados</p>
                            </div>
                          </td>
                        </tr>
                      ) : filteredJobs.map((job) => (
                        <tr key={job.id}>
                          <td className="font-medium">{job.job_name}</td>
                          <td className="text-center">
                            {job.enabled ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <XCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                            )}
                          </td>
                          <td className="text-center">
                            <span className={`badge ${job.last_run_status === 'Succeeded' ? 'badge-success' : job.last_run_status === 'Failed' ? 'badge-error' : 'badge-outline'}`}>
                              {job.last_run_status || '-'}
                            </span>
                          </td>
                          <td className="text-muted-foreground text-sm">{formatDate(job.last_run_date)}</td>
                          <td className="text-right font-mono text-sm text-muted-foreground">
                            {job.last_run_duration ? `${job.last_run_duration}s` : '-'}
                          </td>
                          <td className="text-muted-foreground text-sm">{formatDate(job.next_run_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logins Tab */}
          <TabsContent value="logins">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th>LOGIN</th>
                        <th className="text-center">TIPO</th>
                        <th>DEFAULT DB</th>
                        <th className="text-center">ESTADO</th>
                        <th>ROLES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logins.length === 0 ? (
                        <tr>
                          <td colSpan={5}>
                            <div className="empty-state">
                              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No hay logins registrados</p>
                            </div>
                          </td>
                        </tr>
                      ) : logins.map((login) => (
                        <tr key={login.id}>
                          <td className="font-medium font-mono">{login.login_name}</td>
                          <td className="text-center">
                            <span className="badge badge-outline">{login.login_type}</span>
                          </td>
                          <td className="text-muted-foreground">{login.default_database}</td>
                          <td className="text-center">
                            {login.is_disabled ? (
                              <span className="badge badge-error">Disabled</span>
                            ) : (
                              <span className="badge badge-success">Active</span>
                            )}
                          </td>
                          <td className="text-muted-foreground text-sm">{login.roles || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Linked Servers Tab */}
          <TabsContent value="linkedservers">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {linkedServers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>NOMBRE</th>
                          <th>PROVIDER</th>
                          <th>DATA SOURCE</th>
                          <th className="text-center">RPC OUT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkedServers.map((ls) => (
                          <tr key={ls.id}>
                            <td className="font-medium">{ls.linked_server_name}</td>
                            <td className="text-muted-foreground">{ls.provider}</td>
                            <td className="text-muted-foreground font-mono text-sm">{ls.data_source}</td>
                            <td className="text-center">
                              {ls.is_rpc_out_enabled ? (
                                <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state py-12">
                    <Link2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="empty-state-text">No hay Linked Servers configurados</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DB Mail Tab */}
          <TabsContent value="dbmail">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {dbMail.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>PROFILE</th>
                          <th>ACCOUNT</th>
                          <th>EMAIL</th>
                          <th>MAIL SERVER</th>
                          <th className="text-center">SSL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbMail.map((mail) => (
                          <tr key={mail.id}>
                            <td className="font-medium">{mail.profile_name}</td>
                            <td className="text-muted-foreground">{mail.account_name}</td>
                            <td className="text-muted-foreground">{mail.email_address}</td>
                            <td className="text-muted-foreground font-mono text-sm">{mail.mailserver_name}:{mail.mailserver_port}</td>
                            <td className="text-center">
                              {mail.use_ssl ? (
                                <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state py-12">
                    <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="empty-state-text">No hay Database Mail configurado</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* DB Users Modal */}
        <Dialog open={dbUsersModal.open} onOpenChange={(open) => setDbUsersModal(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Usuarios de {dbUsersModal.dbName}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto flex-1">
              {dbUsersModal.users.length > 0 ? (
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th>USUARIO</th>
                      <th>LOGIN</th>
                      <th className="text-center">TIPO</th>
                      <th>SCHEMA</th>
                      <th>ROLES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbUsersModal.users.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium font-mono">{user.user_name}</td>
                        <td className="text-muted-foreground font-mono text-sm">{user.login_name || '-'}</td>
                        <td className="text-center">
                          <span className="badge badge-outline text-xs">{user.user_type}</span>
                        </td>
                        <td className="text-muted-foreground">{user.default_schema || '-'}</td>
                        <td className="text-muted-foreground text-sm">{user.roles || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state py-8">
                  <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="empty-state-text">No hay usuarios en esta base de datos</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SqlServerDetail;
